import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import NoDataFound from "../../Traffic/NoDataFound";
import { fetchAccessibleGroups } from "../../../../store/features/groupsApi";
import { deleteCanned } from "../../../../store/features/deleteCanned";
import CustomerDeleteModal from "../../People/Customers/CustomerDeleteModal";
import {
  clearCannedMessages,
  clearCannedMessagesError,
  createCannedMessage,
  deleteCannedMessage,
  fetchCannedMessages,
  updateCannedMessage,
} from "../../../../store/features/cannedMessagesApi";
import SettingsContent from "../shared/SettingsContent";
import SettingsPageHeader from "../shared/SettingsPageHeader";
import CannedCreateModal from "./CannedCreateModal";
import { CANNED_OPEN_CREATE_MODAL } from "./cannedEvents";
import {
  buildCreateCannedPayload,
  formatShortcut,
  getErrorMessage,
  humanizeTag,
} from "./cannedUtils";
import { getActiveGroups, getDefaultGroupId } from "../shared/groupUtils";
import CannedCard from "./CannedCard";
import CannedCardSkeleton from "./CannedCardSkeleton";
import CannedGroupSelect from "./CannedGroupSelect";
import CannedSearchBar from "./CannedSearchBar";

function CannedResponsesPage() {
  const dispatch = useDispatch();
  const businessId = useSelector((state) => state.loginToken?.response?.user?.business_id);
  const { list: groups, listLoading } = useSelector((state) => state.peopleGroups);
  const { list: items, loading, saving, error } = useSelector((state) => state.cannedMessages);

  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedGroupId, setSelectedGroupId] = useState("");

  const activeGroups = useMemo(() => getActiveGroups(groups), [groups]);

  const defaultGroupId = useMemo(() => getDefaultGroupId(groups), [groups]);
  const resolvedGroupId = selectedGroupId || defaultGroupId;

  const groupOptions = useMemo(
    () => activeGroups.map((group) => ({ id: group.id, label: group.name })),
    [activeGroups],
  );

  const groupMap = useMemo(
    () => new Map(groupOptions.map((group) => [group.id, group.label])),
    [groupOptions],
  );

  const loadCanned = useCallback(async () => {
    if (!businessId || !resolvedGroupId) return;

    const result = await dispatch(
      fetchCannedMessages({ businessId, groupId: resolvedGroupId }),
    );

    if (!fetchCannedMessages.fulfilled.match(result)) {
      toast.error(getErrorMessage(result.payload));
    }
  }, [businessId, dispatch, resolvedGroupId]);

  useEffect(() => {
    dispatch(fetchAccessibleGroups());
    return () => {
      dispatch(clearCannedMessages());
    };
  }, [dispatch]);

  useEffect(() => {
    if (resolvedGroupId) {
      loadCanned();
    }
  }, [resolvedGroupId, loadCanned]);

  useEffect(() => {
    if (error) {
      toast.error(getErrorMessage(error));
      dispatch(clearCannedMessagesError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    const openCreate = () => {
      setEditingItem(null);
      setModalOpen(true);
    };
    window.addEventListener(CANNED_OPEN_CREATE_MODAL, openCreate);
    return () => window.removeEventListener(CANNED_OPEN_CREATE_MODAL, openCreate);
  }, []);

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return items;
    return items.filter((item) => {
      const title = item.title || humanizeTag(item.shortcut || item?.tagDetails?.tag);
      const shortcut = item.shortcut || item?.tagDetails?.tag || "";
      const content = item.content || "";
      return (
        title.toLowerCase().includes(query) ||
        shortcut.toLowerCase().includes(query) ||
        formatShortcut(shortcut).toLowerCase().includes(query) ||
        content.toLowerCase().includes(query)
      );
    });
  }, [items, search]);

  const selectedGroupName = useMemo(
    () => activeGroups.find((group) => group.id === resolvedGroupId)?.name || "this group",
    [activeGroups, resolvedGroupId],
  );

  const hasSearchQuery = Boolean(search.trim());
  const showEmptyState = !loading && filteredItems.length === 0;

  const handleDeleteRequest = (item) => {
    setDeleteTarget(item);
  };

  const closeDeleteModal = () => {
    setDeleteTarget(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?._id) return;

    try {
      await dispatch(deleteCannedMessage(deleteTarget._id)).unwrap();
      toast.success("Deleted successfully");
      setDeleteTarget(null);
      loadCanned();
    } catch (deleteError) {
      toast.error(getErrorMessage(deleteError));
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingItem(null);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setModalOpen(true);
  };

  const handleSave = async ({ id, title, shortcut, content, groupIds, isPrivate }) => {
    const payload = buildCreateCannedPayload({
      title,
      content,
      shortcut,
      businessId,
      isPrivate,
      groupIds,
    });

    const result = id
      ? await dispatch(updateCannedMessage({ id, payload }))
      : await dispatch(createCannedMessage(payload));

    const action = id ? updateCannedMessage : createCannedMessage;

    if (action.fulfilled.match(result)) {
      if (result.payload?.success) {
        toast.success(
          id ? "Canned response updated successfully" : "Canned response created successfully",
        );
        closeModal();
        loadCanned();
      } else {
        toast.error(
          result.payload?.message || "Canned response already exists for this shortcut.",
        );
      }
    } else {
      toast.error(getErrorMessage(result.payload));
    }
  };

  return (
    <SettingsContent>
      <SettingsPageHeader
        title="Canned Responses"
        description="Save and share pre-written responses for common questions"
      />

      <CannedGroupSelect
        groups={activeGroups}
        value={resolvedGroupId}
        onChange={setSelectedGroupId}
        loading={listLoading}
      />

      <CannedSearchBar value={search} onChange={(e) => setSearch(e.target.value)} />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          mt: "24px",
          width: "100%",
        }}
      >
        {loading ? (
          Array.from({ length: 3 }).map((_, index) => (
            <CannedCardSkeleton key={index} />
          ))
        ) : showEmptyState ? (
          <NoDataFound
            containerStyle={{ height: "280px", minHeight: "200px" }}
            title={
              hasSearchQuery
                ? "No matching canned responses"
                : "No canned responses for this group"
            }
            description={
              hasSearchQuery
                ? `Nothing in ${selectedGroupName} matches "${search.trim()}". Try another search term.`
                : `There are no canned responses for ${selectedGroupName} yet. Use Create Response to add one.`
            }
            showBackButton={false}
          />
        ) : (
          filteredItems.map((item) => (
            <CannedCard
              key={item._id}
              item={item}
              groupMap={groupMap}
              onEdit={handleEdit}
              onDelete={handleDeleteRequest}
            />
          ))
        )}
      </Box>

      <CannedCreateModal
        open={modalOpen}
        onClose={closeModal}
        onConfirm={handleSave}
        groupOptions={groupOptions}
        optionsLoading={listLoading}
        submitting={saving}
        defaultGroupId={resolvedGroupId}
        editingItem={editingItem}
      />

      <CustomerDeleteModal
        open={Boolean(deleteTarget)}
        title="Delete Canned Response"
        customerName={
          deleteTarget
            ? deleteTarget.title || humanizeTag(deleteTarget.shortcut || deleteTarget?.tagDetails?.tag)
            : undefined
        }
        onClose={closeDeleteModal}
        onConfirm={handleDeleteConfirm}
      />
    </SettingsContent>
  );
}

export default CannedResponsesPage;
